import React, {ChangeEvent, FC, ReactNode, SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {connect} from "react-redux";
import './TransactionCard.less';
import Transaction, {TxTypeType} from "../../entities/transaction";
import util from "../../lib/util";
import Loading from "../../ui/Loading";
import '../../stylesheets/form.less';
import * as InvoiceActions from "../../store/actions/invoiceActions";
import * as TransactionActions from "../../store/actions/transactionActions";
import FormElement from "../../lib/FormElement";
import {confirmChangesMade} from "../../ui/Confirm";
import Lead from "../../entities/lead";
import Matter from "../../entities/matter";
import IconButton from "../../ui/IconButton/IconButton";
import Contact from "../../entities/contact";
import WithdrawalCard from "./WithdrawalCard";
import TransferCard from "./TransferCard";
import BalanceAdjustmentCard from "./BalanceAdjustmentCard";
import PaymentCard from "./PaymentCard";
import FinancialHelper from "../FinancialHelper";
import Auth from "../../Auth/Auth";
import {deleteTransaction} from '../../store/deps/transactionDeps';
import i18n from '../../i18n/i18n';

import EntityMeta from "../../ui/EntityMeta/EntityMeta";
import store from "../../store/store";
import {isBalanceAdjustment, isEscrow, isPayment, isTransfer, isWithdrawal} from "./TransactionHelper";
import {formatFML} from "../../Contacts/ContactFormat";
import {getCurrentFirmId, getCurrentUserId, isPortalUser} from "../../lib/picos/currentLogin";
import Icons, {IconType} from "../../ui/IconButton/Icons";
import {startEditingPageF, stopEditingPageF} from "../../lib/pageEditing";
import {RootDispatch, RootState} from "../../@types/store";
import Invoice from "../../entities/invoice";
import {uuid} from "../../@types/entity";
import Alert from "../../lib/alert";

import {EditableTableCardProps} from "../../ui/EditableTable/EditableTable";
import EscrowCard from "./EscrowCard";
import useIsMounted from "../../hooks/useIsMounted";
import {VoidCallback} from "../../@types/functions";
import useComponentCache from "../../hooks/useComponentCache";
import useStateWithCallback from "../../hooks/useStateWithCallback";
import deepEqual from "fast-deep-equal/es6";
import {useRootDispatch, useRootSelector} from "../../hooks/useOutlawStore";

export type OnTransactionChange = (tr: Transaction, saved?: boolean) => void;

export type TransactionChildCardProps = EditableTableCardProps<Transaction> & {
    // Callback for changes (don't use local state)
    onChange: (tr: Transaction, save?: boolean) => void,

    // Has this transaction been changed?
    hasChanged: boolean,

    // If called from LeadCard
    lead?: Lead,

    // Is this the lead's paid initial consult? See TransactionCardProps. (OWA-957)
    isConsultPayment?: boolean,

    // If called from CaseCard
    matter?: Matter,

    // If called from ContactCard
    client?: Contact,

    // Transaction type
    txType: TxTypeType,

    // FormElement
    element: FormElement,

    // Is this transaction read-only? (not editable)
    readOnly?: boolean,

    // The current Transaction object. Optional by design: each child card resolves its
    // own non-null `current` in code (`props.current ?? new Transaction()`), so the
    // guarantee lives in the code itself, not in this type contract. The parent
    // TransactionCard always passes a resolved current — but no child depends on it. (OWA-490/OWA-552)
    current?: Transaction,

    // Only show info, do not allow editing
    infoOnly?: boolean,

    // Is this card embedded in a modal dialog?
    portaled?: boolean,

    // Called if we cancel out
    onCancel?: VoidFunction,
};

export type TransactionCardProps = EditableTableCardProps<Transaction> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps> &
    {
        // Transaction amount, defaults to 0 if not specified
        amount?: number,

        contact?: Contact,
        lead?: Lead, // If called from Lead card
        matter?: Matter, // If called from Case card
        client?: Contact, // If called from ContactCard

        // Is this the lead's paid initial consult (LeadCard's Take Payment)? Only then does the
        // client's receipt say the funds paid for the consult. Other lead payments are fees or
        // retainers. (OWA-957)
        isConsultPayment?: boolean,

        // The type of transaction we are editing. This is used to determine which components to display and which fields to show.
        param?: TxTypeType,

        // Function to call to start editing a transaction.  If not provided, we will use the default startEditing method.
        startEditing?: VoidFunction,

        // Are we in read-only mode?
        readOnly?: boolean,

        // Is this embedded outside an EditableTable? If so, only show basic info
        infoOnly?: boolean,

        // If called in a dialog
        onClose?: VoidCallback,
    };

type TransactionChoice = {
    code: string,
    icon: IconType,
    name: string,
    description: ReactNode,
    presets?: {
        paymentMethod?: string,
        source?: string,
        sourceType?: string,
        target?: string,
        targetType?: string,
    },
}

type TransactionCardState = {
    hasChanged: boolean,
    okError: Error | any | null,
    // 'current' is cached ONLY for an in-progress (hasChanged) draft AND only when the cache
    // key is stable (an existing transaction id or a generated newId). Without a stable key the
    // key collapses to `transaction-card-undefined`, shared across every keyless new-payment
    // dialog, and caching `current` there bled one dialog's payment method into the next — the
    // original reason this was never cached. With a stable key we preserve the user's unsaved
    // draft across an unmount/remount (e.g. switching tabs away and back). (OWA-490)
    current?: Transaction,
}

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works, and which
 * bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props are defaulted
 * so that coming in from outside an EditableTable still works.
 *
 *
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a Matter or Contact.
 * Check props.env.matter and props.env.contact to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter is unchangeable, but the contact (if > 1) is.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the Matter is.
 *
 * For a payment, we show a contact first, then list their cases. Click on the cases that we will apply the funds to
 */
const TransactionCard = (props: TransactionCardProps) => {
    const TransactionChoices: TransactionChoice[] = useMemo(() => [
        {
            code: 'FR',
            icon: Icons.PAYMENT,
            name: i18n.ls('transaction', 'FR'),
            description: i18n.d('transaction', 'FR')
        },
        {
            code: 'PT',
            icon: Icons.PAYMENT,
            name: i18n.ls('transaction', 'PT'),
            description: i18n.d('transaction', 'PT'),
            presets: {
                paymentMethod: 'trust',
                targetType: 'B',
                target: FinancialHelper.getOperatingAccount()?.id
            }
        },
        {
            code: 'PC',
            icon: Icons.PAYMENT,
            name: i18n.ls('transaction', 'PC'),
            description: i18n.d('transaction', 'PC'),
            presets: {
                targetType: 'B',
                target: FinancialHelper.getOperatingAccount()?.id
            }
        },
        {
            code: 'RR',
            icon: Icons.WITHDRAW_FUNDS,
            name: i18n.ls('transaction', 'RR'),
            description: i18n.d('transaction', 'RR')
        },
        {
            code: 'RO',
            icon: Icons.WITHDRAW_FUNDS,
            name: i18n.ls('transaction', 'RO'),
            description: i18n.d('transaction', 'RO'),
            presets: {
                sourceType: 'B',
                source: FinancialHelper.getOperatingAccount()?.id
            }
        },
        {
            code: 'DT',
            icon: Icons.WITHDRAW_FUNDS,
            name: i18n.ls('transaction', 'DT'),
            description: i18n.d('transaction', 'DT')
        },
        {
            code: 'TT',
            icon: Icons.TRANSFER_FUNDS,
            name: i18n.ls('transaction', 'TT'),
            description: i18n.d('transaction', 'TT')
        },
        {
            code: 'ED',
            icon: Icons.ESCROW,
            name: i18n.ls('transaction', 'ED'),
            description: i18n.d('transaction', 'ED')
        },
        {
            code: 'DE',
            icon: Icons.ESCROW,
            name: i18n.ls('transaction', 'DE'),
            description: i18n.d('transaction', 'DE')
        },
        {
            code: 'BA',
            icon: Icons.BALANCE_ADJUSTMENT,
            name: i18n.ls('transaction', 'BA'),
            description: i18n.d('transaction', 'BA')
        },
        {
            code: 'ST',
            icon: Icons.BALANCE_ADJUSTMENT,
            name: i18n.ls('transaction', 'ST'),
            description: i18n.d('transaction', 'ST'),
            presets: {
                targetType: 'M',
                target: props.matter?.id
            }
        },
        {
            code: 'SA',
            icon: Icons.BALANCE_ADJUSTMENT,
            name: i18n.ls('transaction', 'SA'),
            description: i18n.d('transaction', 'SA'),
            presets: {
                targetType: 'M'
            }
        }
    ], [props.matter]);

    const isMounted = useIsMounted();
    const doResetRef = useRef<boolean>(false);

    const propsOnCancel = props.onCancel;
    const propsOnOk = props.onOk;
    const propsOnClose = props.onClose;
    const propsCurrent = props.current;

    const {cacheSave, cacheReset, cacheValue} = useComponentCache<TransactionCardState>(`transaction-card-${props.current?.id || props.newId}`);

    const dispatch = useRootDispatch();

    const propInvoices = useRootSelector(state => state.invoices);

    const [hasChanged, setHasChanged] = useState<boolean>(cacheValue('hasChanged', false));
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);  // In flight: never cached, or a remount restores it and stays disabled (OWA-1355)
    const [okError, setOkError] = useState<Error | any | null>(cacheValue('okError', null));

    // A draft is only safe to cache/restore when the cache key is stable (see
    // TransactionCardState). restoringDraft is true when we have a cached, modified draft to
    // bring back on this mount (e.g. after switching tabs away and back). (OWA-490)
    const hasStableCacheKey = !!(props.current?.id || props.newId);
    const cachedDraft = cacheValue('current');
    const restoringDraft = hasStableCacheKey && cacheValue('hasChanged', false) && !!cachedDraft;

    const lead = props.lead || props.env?.lead;

    // A new transaction is dated now, including a payment on a lead. Lead payments used to be
    // backdated to the initial consult date, which recorded card charges taken months after the
    // consult as if they were paid at the consult. (OWA-957)
    const txDate = new Date();

    // Normally we use an amount of $0
    let amount: number;

    // A lead's Take Payment passes the paidConsultPrice option as the amount.
    if (lead) amount = props.amount;
    else amount = props.amount ?? 0;

    // Prefer the cached in-progress draft (only when restoringDraft — i.e. modified + stable
    // key); otherwise use props.current, or build a fresh Transaction. (OWA-490)
    const [current, setCurrent] = useStateWithCallback<Transaction>(
        restoringDraft && cachedDraft
                      ? cachedDraft
                      : props.current ? props.current
                      : new Transaction({
                          transactionId: props.newId,
                          firmId: getCurrentFirmId(),
                          addedBy: getCurrentUserId(),
                          addedOn: util.newHRTimestamp(),
                          status: 'C',
                          txType: props.param,
                          txDate,
                          amount,
                          clientIds: props.env?.matter ? props.env.matter.clients : lead ? lead.clients : props.matter ? props.matter.clients : [],
                          matterIds: props.env?.matter ? (props.env.matter.id ? [props.env.matter.id] : []) : lead ? [lead.id] : props.matter ? [props.matter.id] : [],
                          sourceType: lead ? 'C' : null,
                          targetType: lead ? 'B' : null,
                          refusedDate: null,
                          voidedDate: null,
                          entityType: lead ? 'lead' : 'transaction',
                          entityId: lead?.id || props.newId
                      })
    );

    /**
     * Get the currently selected transaction type
     */
    const getCurrentTransactionTypeInfo = useCallback(() => {
        return TransactionChoices.find(choice => choice.code === current.txType);
    }, [TransactionChoices, current.txType]);

    if (!restoringDraft && !props.current && props.param?.length === 2) {
        // If creating anew, and we have a full transaction type, apply presets.
        // Skip when restoring a draft so we don't overwrite the user's edited
        // target/targetType/paymentMethod with the type defaults. (OWA-490)
        const info = getCurrentTransactionTypeInfo();
        Object.assign(current, info.presets);
    }

    // New-transaction matterIds/clientIds are seeded from props.env.matter in the
    // useState initializer above (clone-free, once at mount) — do not re-write the
    // state-held `current` Transaction during render.

    useEffect(() => {
        // In rare cases where the bank accounts haven't loaded yet.
        if (FinancialHelper.getOperatingAccount()) {
            TransactionChoices.filter(tc => tc.presets?.targetType === 'B' && !tc.presets?.target)
                .forEach(tc => {
                    console.warn(`No target account found for bank account ${tc.presets?.target}, updating to operating account...`);
                    tc.presets.target = FinancialHelper.getOperatingAccount()?.id ?? ''
                });
        }
    }, [TransactionChoices]);

    useLayoutEffect(() => {
        // Cache the draft 'current' ONLY for a modified transaction with a stable key, so an
        // unmount/remount (e.g. tab switch) restores the user's unsaved work. Otherwise leave
        // 'current' out of the entry — see the comment on TransactionCardState. cacheSave
        // OVERWRITES the entry, so omitting it is what keeps a clean entity uncached. (OWA-490)
        cacheSave(hasChanged && hasStableCacheKey
                  ? {hasChanged, okError, current}
                  : {hasChanged, okError});

        return () => {
            if (doResetRef.current) cacheReset();
        }
    }, [cacheReset, cacheSave, current, hasChanged, hasStableCacheKey, okError]);

    const okErrorConfirmed = useCallback((_e: SyntheticEvent) => {
            if (isMounted()) setOkError(null);
        }, [isMounted, setOkError]);

    /**
     * Handle normal field changes.
     *
     * @param {ChangeEvent<HTMLInputElement>} e
     * @param {string} field
     * @param {string} text
     */
    const handleTextChange = useCallback((e: ChangeEvent<HTMLInputElement>, field: string, text: string) => {
        e.preventDefault();

        const transaction = current.clone();
        if (field === 'amount') transaction.amount = Number.parseFloat(text || e.target.value);
        else transaction[field] = (text || e.target.value) ?? '';

        if (isMounted()) {
            setCurrent(transaction);
            setHasChanged(true);
        }
    }, [current, isMounted, setCurrent, setHasChanged]);

    /**
     * Callback from a card.
     * @param {Transaction} tr
     * @param {boolean} saved - has this already been saved?
     */
    const onCardChange = useCallback((tr: Transaction, save?: boolean) => {
        if (isMounted() && !save) {
            if (!deepEqual(tr, current)) {
                // Mark as changed if not saved (saved means it has already been saved).
                tr.addedBy = getCurrentUserId();
                tr.addedOn = util.newHRTimestamp();

                setCurrent(tr, () => {
                    if (!isPortalUser()) startEditingPageF('transaction', current.id);
                });

                setHasChanged(true);
            }
        }
    }, [current, isMounted, setCurrent]);

    const onCancel = useCallback(() => {
        const _doWork = () => {
            doResetRef.current = true;

            stopEditingPageF('transaction', current.id);

            try {
                propsOnCancel();

                // If called from dialog, call onClose
                propsOnClose?.();
            }
            catch (err) {
                console.error('TransactionCard::onOk::props.onCancel/Close returned', err);
                if (isMounted()) setOkError(err);
            }
            finally {
                if (isMounted() && isSubmitting) setIsSubmitting(false);
            }
        };

        if (hasChanged) {
            // Ask if they really want to save
            confirmChangesMade(_doWork);
        }
        else {
            _doWork();
        }
    }, [current.id, hasChanged, isMounted, isSubmitting, propsOnCancel, propsOnClose]);

    const onOk = useCallback((e: SyntheticEvent) => {
        e?.preventDefault();

        doResetRef.current = true;

        const transaction = current;

        // Check if the transaction is valid
        if (FinancialHelper.transactionFailureReason(transaction) !== null) {
            // This means either the sourceType or targetType is not valid for the transaction type
            const txType = transaction.txType;
            const sourceType = transaction.sourceType;
            const targetType = transaction.targetType;

            console.error(`Invalid transaction type for ${transaction.id}: ${txType}, sourceType: ${sourceType}, targetType: ${targetType}`);
        }

        if (!lead && !props.matter && !props.contact) stopEditingPageF('transaction', current.id);

        try {
            propsOnOk(transaction);

            if (isMounted()) setIsSubmitting(true);

            if (propsOnClose) propsOnClose();
        }
        catch (err) {
            console.error('TransactionCard::onOk::props.onOk returned', err);
            if (isMounted()) setOkError(err);
        }
        finally {
            if (isMounted()) setIsSubmitting(false);
        }
    }, [current, isMounted, lead, props.contact, props.matter, propsOnClose, propsOnOk]);

    /*
     * - FR Fund Retainer: C->G,M, where C is client or responsible payer [Payment]
     * - FO Fund Retainer: B->G,M, where B is an operating account [Payment]
     * - ED Escrow Deposit: C->E, where C is 3rd party (usually) [Escrow]
     * - EP Escrow Payment (Disbursement): E-C, where C is a 3rd party (usually) [Escrow]
     * - PT Payment: G,M->B, where B is operating [Transfer]
     * - PC Payment: C->B, where B is operating [Payment]
     * - RR Refund Retainer: G,M->C, where C is client [Withdrawal]
     * - DT Disburse Trust: G,M,E->C, where C is 3rd party [Withdrawal]
     * - DE Disburse Escrow: E->C [Escrow]
     * - TT Trust Transfer: C,M,E->C,M,E, where C is a client.  There is no E->E or C->C! [Transfer]
     * - BA Balance Adjustment: These happen in-place, there is no movement of funds. [Balance Adjustment]
     */
    const renderTransactionChoice = useCallback(() => {
        const onClick = (_e: SyntheticEvent, code: string, presets: any) => {
            const _current = current.clone();
            _current.txType = code;

            if (presets) Object.assign(_current, presets);
            if (code === 'SA' && props.matter) {
                _current.target = props.matter.id;
            }
            else if (code === 'BA' && props.matter) {
                _current.targetType = 'M';
                _current.target = props.matter.id;
            }

            if (isMounted()) {
                setCurrent(_current);
            }
        };

        return (
            <div className='transaction-card chooser'>
                <div className='transaction-card__make-choice'>
                    {TransactionChoices
                        .filter(choice => {
                            switch (props.param) {
                                case 'P':
                                    return isPayment(choice.code);
                                case 'W':
                                    return isWithdrawal(choice.code);
                                case 'E':
                                    return isEscrow(choice.code);
                                case 'A':
                                    return isBalanceAdjustment(choice.code);
                                default:
                                    throw new Error(i18n.ds('transaction', 'errUnknownChoice') + choice);
                            }
                        })
                        .map((choice, index) => (
                            <div key={`choice${index}`}
                                 className='transaction-card__choice'
                            >
                                <IconButton key={`choice-${index}-button`}
                                            type={choice.icon}
                                            label={choice.name}
                                            labelBold={true}
                                            onClick={(e) => onClick(e, choice.code, choice.presets)}
                                />
                                <div key={`choice-${index}-details`}
                                     className='transaction-card__choice-description'
                                     onClick={e => onClick(e, choice.code, choice.presets)}>
                                    {choice.description}
                                </div>
                            </div>
                        ))}

                    {/* Cancel out */}
                    <IconButton key='cancel'
                                id='TransactionCancel'
                                type={Icons.SEARCH_CANCEL}
                                label={i18n.c('cancel')}
                                color='red'
                                onClick={propsOnCancel}
                    />
                </div>
            </div>
        );
    }, [TransactionChoices, current, isMounted, props.matter, propsOnCancel, props.param, setCurrent]);

    /**
     * Remove this transaction from an invoice, then optionally delete.
     *
     * @param {boolean} [doDelete]
     */
    const removeFromInvoice = useCallback((doDelete?: boolean) => {
        const tx = propsCurrent.clone();

        // Find the related invoice and remove from list of transactions (if finalized)
        const invoice = propInvoices.get(tx.invoiceId);

        if (!!invoice?.finalizedOn) {
            const inv = invoice.clone();
            inv.addedOn = util.newHRTimestamp();
            inv.addedBy = getCurrentUserId();
            inv.status = 'U';
            // eslint-disable-next-line no-restricted-syntax -- surgical un-association of one tx from a FINALIZED invoice: this updates the invoice's forward array AND the tx's back-pointer is cleared below, so both sides stay in sync. invoiceAssociation helpers intentionally throw for finalized single-entity removal.
            inv.transactionIds = invoice.transactionIds.filter(tid => tid !== tx.id);

            dispatch(InvoiceActions.saveInvoiceQueue(inv));
        }

        if (doDelete) {
            deleteTransaction(tx);

            propsOnCancel();
        }
        else {
            // Update the transaction to remove the related invoiceId
            tx.addedOn = util.newHRTimestamp();
            tx.addedBy = getCurrentUserId();
            tx.status = 'U';
            // eslint-disable-next-line no-restricted-syntax -- clears this tx's back-pointer; the paired invoice.transactionIds update is above (finalized invoices) or unnecessary (drafts store no associations).
            tx.invoiceId = null;

            dispatch(TransactionActions.saveTransactionQueue(tx));

            setCurrent(tx);
        }
    }, [dispatch, propInvoices, propsCurrent, propsOnCancel, setCurrent]);

    const removeFromInvoiceAndEdit = useCallback(() => removeFromInvoice(), [removeFromInvoice]);
    const removeFromInvoiceAndDelete = useCallback(() => removeFromInvoice(true), [removeFromInvoice]);

    // If we do not yet have a specific transaction type, ask for clarification.
    if ((current.txType?.length ?? 0) < 2) {
        return renderTransactionChoice();
    }

    // Check to see if we got an error while trying to save. If so, put the error into errorMessage for display below
    let errorMessage = '';
    if (okError && okError.message) {
        if (okError.message.clientMessage) errorMessage = okError.message.clientMessage;
        else errorMessage = okError.message;
        Alert.addSimpleError(errorMessage);
    }

    // We now have a specific two-character transaction type

    // If we are submitting, show the Loading spinner.  This is usually so fast you will never see it.
    if (isSubmitting) return (
        <div className="transaction-card">
            <Loading/>
        </div>
    );
    else {
        const element = new FormElement('transaction-card', current, handleTextChange, handleTextChange);

        const txType: TxTypeType = current.txType;

        // Transaction choices used to display
        const choice = getCurrentTransactionTypeInfo();

        const Card: FC<TransactionChildCardProps> =
            isPayment(txType) ? PaymentCard :
            isWithdrawal(txType) ? WithdrawalCard :
            isTransfer(txType) ? TransferCard :
            isBalanceAdjustment(txType) ? BalanceAdjustmentCard :
            isEscrow(txType) ? EscrowCard : null;

        return (
            <div className={`transaction-card transaction-card__${txType}`}>

                {/* Title bar: what kind of transaction is this? */}
                {!props.infoOnly &&
					<div className={`transaction-card__title transaction-card__title-${choice.code}`}>
                        {choice.name}
					</div>}

                {/* Form fields */}
                <form className="Form" onSubmit={onOk}>
                    {Auth.isOwner() && current.invoiceId && <div className='transaction-card__forced'>
                        {i18n.l('transaction', 'modifyNotice')}
						<IconButton type={Icons.EDIT}
						            label={i18n.l('transaction', 'edit')}
						            onClick={removeFromInvoiceAndEdit}
						/>
						<IconButton type={Icons.DELETE}
						            label={i18n.l('transaction', 'delete')}
						            onClick={removeFromInvoiceAndDelete}
						/>
					</div>}

                    <Card txType={txType}
                          current={current}
                          element={element}
                          lead={lead}
                          isConsultPayment={props.isConsultPayment}
                          matter={props.matter || props.env?.matter}
                          client={props.client || props.env?.client}
                          onOk={onOk}
                          hasChanged={hasChanged}
                          onCancel={onCancel}
                          onChange={onCardChange}
                          readOnly={props.readOnly}
                          portaled={props.embedded}
                          infoOnly={props.infoOnly}
                    />

                    {/* History */}
                    <EntityMeta entityType="transaction"
                                entity={current}
                                hidden={false}
                                onVersionChange={(e) => setCurrent(e as Transaction)}
                    />
                </form>

                {/* If we found an error while saving, display it here.*/}
                {okError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <strong>{i18n.c('error')}{errorMessage}</strong>
                        <button type="button"
                                className="close" data-dismiss="alert"
                                onClick={okErrorConfirmed}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        bankAccounts: state.bankAccounts,
        transactions: state.transactions,
        invoices: state.invoices
    };
};

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveInvoiceQueue: (invoice: Invoice) => dispatch(InvoiceActions.saveInvoiceQueue(invoice)),
        onSaveTransactionQueue: (tx: Transaction) => dispatch(TransactionActions.saveTransactionQueue(tx))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TransactionCard);
